import { z } from 'zod';
import { InvalidStatusTransitionError } from './errors.js';
import type { Permission } from './permissions.js';

export const DONATION_BOX_STATUSES = ['in_stock', 'placed', 'collected', 'retired'] as const;
export type DonationBoxStatus = (typeof DONATION_BOX_STATUSES)[number];
export const donationBoxStatusSchema = z.enum(DONATION_BOX_STATUSES);

export const DONATION_BOX_ACTIONS = ['place', 'collect', 'retire'] as const;
export type DonationBoxAction = (typeof DONATION_BOX_ACTIONS)[number];
export const donationBoxActionSchema = z.enum(DONATION_BOX_ACTIONS);

export type DonationBoxEdge = {
  readonly action: DonationBoxAction;
  readonly from: DonationBoxStatus;
  readonly to: DonationBoxStatus;
  readonly permission: Permission;
};

/**
 * A box cycles: it is placed with a keeper, emptied, and placed again, and each
 * emptying is a `donation_box_collections` row rather than an edit of the one
 * the legacy kept. `retired` is the only terminal state, reachable from every
 * live one, because a box is lost or destroyed at any point in the cycle.
 */
const DONATION_BOX_EDGES: readonly DonationBoxEdge[] = [
  { action: 'place', from: 'in_stock', to: 'placed', permission: 'donationBox.edit' },
  { action: 'place', from: 'collected', to: 'placed', permission: 'donationBox.edit' },
  { action: 'collect', from: 'placed', to: 'collected', permission: 'donationBox.collect' },
  { action: 'retire', from: 'in_stock', to: 'retired', permission: 'donationBox.edit' },
  { action: 'retire', from: 'placed', to: 'retired', permission: 'donationBox.edit' },
  { action: 'retire', from: 'collected', to: 'retired', permission: 'donationBox.edit' },
];

export function donationBoxEdge(
  from: DonationBoxStatus,
  action: DonationBoxAction,
): DonationBoxEdge | null {
  return DONATION_BOX_EDGES.find((edge) => edge.from === from && edge.action === action) ?? null;
}

export function donationBoxEdgesFrom(from: DonationBoxStatus): readonly DonationBoxEdge[] {
  return DONATION_BOX_EDGES.filter((edge) => edge.from === from);
}

export function canTransitionDonationBox(from: DonationBoxStatus, to: DonationBoxStatus): boolean {
  return DONATION_BOX_EDGES.some((edge) => edge.from === from && edge.to === to);
}

export function assertDonationBoxTransition(from: DonationBoxStatus, to: DonationBoxStatus): void {
  if (!canTransitionDonationBox(from, to)) {
    throw new InvalidStatusTransitionError('donationBox.status', from, to);
  }
}

export function nextDonationBoxStatuses(from: DonationBoxStatus): readonly DonationBoxStatus[] {
  return donationBoxEdgesFrom(from).map((edge) => edge.to);
}

export function isTerminalDonationBoxStatus(status: DonationBoxStatus): boolean {
  return donationBoxEdgesFrom(status).length === 0;
}

/** A box out with a keeper is the one state the boxes-out report is about. */
export function isDonationBoxPlaced(status: DonationBoxStatus): boolean {
  return status === 'placed';
}

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * How long a placed box has gone unemptied, counted from the later of its
 * placement and its last collection — the operational question the legacy shape
 * could not answer, because a box was a donation row that had already been
 * emptied. A box that is not out answers null rather than zero: nothing is
 * outstanding.
 */
export function daysSinceLastCollection(box: {
  status: DonationBoxStatus;
  placedAt: Date | null;
  lastCollectedAt: Date | null;
  now: Date;
}): number | null {
  if (!isDonationBoxPlaced(box.status)) return null;
  const since = latest(box.placedAt, box.lastCollectedAt);
  if (!since) return null;
  const elapsed = box.now.getTime() - since.getTime();
  return elapsed <= 0 ? 0 : Math.floor(elapsed / MILLISECONDS_PER_DAY);
}

function latest(left: Date | null, right: Date | null): Date | null {
  if (!left) return right;
  if (!right) return left;
  return left.getTime() >= right.getTime() ? left : right;
}
